import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, initials } from "@/lib/constants";
import EnvBanner from "@/components/EnvBanner";
import {
  LayoutDashboard, FilePlus2, GitBranch, Archive, BarChart3, Settings,
  Bell, Search, LogOut, Sparkles, Shield, Users2, Rows3, Rows4, KeyRound, Building2, FolderHeart, UserCheck, Briefcase, Scale, Workflow, ShoppingCart, FolderTree, UserSquare2, ClipboardList, RefreshCw, Eye, X, Inbox, FileText, Upload, FileDown
} from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import AISearchPanel from "@/components/AISearchPanel";

// Grouped, role-aware navigation. Each group renders as a subtle section header.
const NAV_GROUPS = [
  {
    label: "Workspace",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard },
      { to: "/my-contracts", label: "My Contracts", icon: FolderHeart },
      { to: "/queue", label: "My Queue", icon: Inbox,
        roles: ["contract_analyst","attorney","legal_manager","general_counsel","records_management","it_admin"] },
      { to: "/intake", label: "New Intake", icon: FilePlus2 },
      { to: "/workspace/paralegal", label: "My Workspace", icon: Briefcase,
        roles: ["contract_analyst"] },
      { to: "/portal/purchasing", label: "Purchasing Portal", icon: ShoppingCart,
        roles: ["purchasing"] },
      { to: "/pipeline", label: "Routing Pipeline", icon: GitBranch,
        roles: ["general_counsel","legal_manager","attorney","contract_analyst","compliance","it_admin","records_management","purchasing"] },
      { to: "/repository", label: "Contract Repository", icon: FolderTree },
      { to: "/referrals", label: "Referrals Inbox", icon: Inbox },
      { to: "/renewals", label: "Renewal Pipeline", icon: RefreshCw,
        roles: ["it_admin","legal_manager","general_counsel","attorney","contract_analyst","department_director","compliance"] },
    ],
  },
  {
    label: "Insight",
    items: [
      { to: "/compliance", label: "Compliance", icon: Shield, roles: ["compliance","general_counsel","it_admin"] },
      { to: "/reports", label: "Reports & BI", icon: BarChart3,
        roles: ["general_counsel","legal_manager","it_admin","compliance","department_director"] },
      { to: "/reports/spend", label: "Spend Report", icon: BarChart3,
        roles: ["general_counsel","legal_manager","it_admin","purchasing","department_director"] },
      { to: "/reports/leadership", label: "Leadership Dashboard", icon: BarChart3,
        roles: ["general_counsel","legal_manager","it_admin","compliance","department_director"] },
      { to: "/ai", label: "AI Assistant", icon: Sparkles,
        roles: ["general_counsel","legal_manager","attorney","contract_analyst","it_admin","compliance"] },
      { to: "/university", label: "Contract University", icon: Sparkles },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/admin/access", label: "Access Control", icon: KeyRound,
        roles: ["it_admin","department_admin","department_director"] },
      { to: "/admin/masters", label: "Sites & Vendors", icon: Building2,
        roles: ["it_admin"] },
      { to: "/admin/workflows", label: "Approval Workflows", icon: Workflow,
        roles: ["it_admin"] },
      { to: "/admin/reviewer-slots", label: "Reviewer Roster", icon: UserSquare2,
        roles: ["it_admin","legal_manager","general_counsel"] },
      { to: "/admin/evaluation-rules", label: "Evaluation Rules", icon: ClipboardList,
        roles: ["it_admin","general_counsel","attorney","legal_manager"] },
      { to: "/admin/milestone-templates", label: "Milestone Templates", icon: ClipboardList,
        roles: ["it_admin"] },
      { to: "/admin/message-templates", label: "Message Templates", icon: ClipboardList,
        roles: ["it_admin","legal_manager","general_counsel"] },
      { to: "/admin/standards", label: "HCD Standards Library", icon: FileText,
        roles: ["general_counsel","legal_manager","it_admin"] },
      { to: "/admin/hierarchy", label: "Repository Hierarchy", icon: FolderTree,
        roles: ["general_counsel","legal_manager","it_admin"] },
      { to: "/admin/flight", label: "Flight Status Display", icon: Sparkles,
        roles: ["general_counsel","legal_manager","it_admin"] },
      { to: "/admin/notifications", label: "Notifications & Reassign", icon: Bell,
        roles: ["general_counsel","legal_manager","it_admin"] },
      { to: "/admin/c360-import", label: "C360 Import", icon: Upload,
        roles: ["general_counsel","legal_manager","it_admin"] },
      { to: "/admin/data-export", label: "Data Export & Backups", icon: FileDown,
        roles: ["general_counsel","legal_manager","it_admin"] },
      { to: "/admin/roles", label: "Role Library", icon: Users2,
        roles: ["it_admin"] },
      { to: "/admin/users", label: "User Table", icon: Users2,
        roles: ["it_admin"] },
      { to: "/audit", label: "Audit Viewer", icon: Shield,
        roles: ["it_admin","general_counsel","legal_manager","compliance"] },
      { to: "/admin", label: "IT Admin", icon: Shield, roles: ["it_admin"] },
    ],
  },
];

function hasRole(user, roles) {
  if (!roles) return true;
  if (!user) return false;
  // F-5: honour primary + delegated roles for menu visibility. Removed the
  // IT-Admin superuser bypass — the sidebar now shows exactly what the user's
  // roles allow, consistent with backend enforcement.
  const owned = new Set([user.role, ...(user.roles || [])]);
  return roles.some(r => owned.has(r));
}

export default function Layout({ children }) {
  const { user, realUser, logout, impersonate, endImpersonation, impersonatedRole, impersonationExpiresAt } = useAuth();
  const nav = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [queueCount, setQueueCount] = useState(0);
  const [search, setSearch] = useState("");
  const [searchMode, setSearchMode] = useState("search"); // Phase 2: "search" | "ai"
  const [aiOpen, setAiOpen] = useState(false);
  const [aiQuery, setAiQuery] = useState("");
  const [density, setDensity] = useState(() => localStorage.getItem("hcd_density") || "comfortable");
  const [impPickerOpen, setImpPickerOpen] = useState(false);
  const [impSecondsLeft, setImpSecondsLeft] = useState(0);

  useEffect(() => {
    if (!impersonationExpiresAt) { setImpSecondsLeft(0); return; }
    const tick = () => setImpSecondsLeft(Math.max(0, Math.round((impersonationExpiresAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [impersonationExpiresAt]);

  useEffect(() => {
    document.body.classList.toggle("density-compact", density === "compact");
    localStorage.setItem("hcd_density", density);
  }, [density]);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/notifications"); setNotifs(r.data || []); } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    const legal = ["contract_analyst","attorney","legal_manager","general_counsel","records_management","it_admin"];
    if (!legal.includes(user.role)) return;
    const load = () => api.get("/queue/summary").then(r => setQueueCount(r.data?.count || 0)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [user]);

  const unread = notifs.filter(n => !n.read).length;

  const handleSearch = (e) => {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;
    // Phase 2 — Ask AI tab OR auto-detect NL queries route to AI panel.
    const looksNL = /\b(over|above|expire|expiring|renew|greater|less than|for)\b/i.test(q) || q.length > 40;
    if (searchMode === "ai" || looksNL) {
      setAiQuery(q); setAiOpen(true); return;
    }
    nav(`/pipeline?search=${encodeURIComponent(q)}`);
  };

  const visibleGroups = NAV_GROUPS
    .map(g => ({ ...g, items: g.items.filter(i => hasRole(user, i.roles)) }))
    .filter(g => g.items.length > 0);

  return (
    <div className="flex flex-col h-screen bg-slate-50">
      <EnvBanner />
      <div className="flex flex-1 overflow-hidden">
      {impersonatedRole ? (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white text-xs font-semibold px-4 py-2 flex items-center gap-3 shadow" data-testid="impersonate-banner">
          <Eye className="w-4 h-4" />
          <span>Viewing as <b>{ROLE_LABELS[impersonatedRole] || impersonatedRole}</b> — auto-exit in {impSecondsLeft}s</span>
          <button onClick={endImpersonation} data-testid="impersonate-end"
                  className="ml-auto h-6 px-2 rounded bg-white/20 hover:bg-white/30 flex items-center gap-1">
            <X className="w-3 h-3"/> End
          </button>
        </div>
      ) : null}
      {/* Sidebar */}
      <aside className={`w-60 flex-shrink-0 bg-[#0F172A] text-white flex flex-col ${impersonatedRole ? "mt-8" : ""}`} data-testid="app-sidebar">
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#005289] flex items-center justify-center text-white font-bold text-xs">HCD</div>
            <div>
              <div className="text-sm font-bold leading-none">Contract Central</div>
              <div className="text-[10px] text-slate-400 mt-1">Legal Department</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 py-3 custom-scroll overflow-y-auto" data-testid="sidebar-nav">
          {visibleGroups.map(g => (
            <div key={g.label} className="mb-2">
              <div className="px-5 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{g.label}</div>
              {g.items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/"}
                  className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                  data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon className="icon" />
                  <span>{label}</span>
                  {to === "/queue" && queueCount > 0 && (
                    <span className="ml-auto text-[10px] font-bold bg-[#005289] text-white rounded-full px-1.5 py-0.5"
                          data-testid="queue-count-badge">{queueCount}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="border-t border-slate-800 p-3">
          {realUser?.role === "it_admin" ? (
            <div className="mb-2 relative">
              <button onClick={() => setImpPickerOpen(v => !v)} data-testid="impersonate-open"
                      className="w-full text-[11px] text-slate-300 hover:text-white flex items-center gap-1 py-1">
                <Eye className="w-3 h-3"/> {impersonatedRole ? `Impersonating: ${ROLE_LABELS[impersonatedRole] || impersonatedRole}` : "View as role…"}
              </button>
              {impPickerOpen ? (
                <div className="absolute bottom-8 left-0 right-0 bg-white text-slate-800 border border-slate-200 rounded shadow-lg z-50 max-h-64 overflow-y-auto text-xs" data-testid="impersonate-picker">
                  {["general_counsel","legal_manager","attorney","contract_analyst","compliance","records_management","purchasing","department_director","department_admin","business_unit_rep","readonly_auditor"].map(r => (
                    <button key={r} onClick={() => { impersonate(r, 30); setImpPickerOpen(false); }}
                            className="w-full text-left px-3 py-2 hover:bg-slate-100" data-testid={`impersonate-${r}`}>
                      {ROLE_LABELS[r] || r} <span className="text-slate-400 font-mono text-[10px]">({r})</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#005289] flex items-center justify-center text-xs font-bold overflow-hidden">
              {user?.avatar_url ? <img src={user.avatar_url} alt="" className="w-full h-full object-cover" /> : initials(user?.name)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] text-slate-400 truncate">{ROLE_LABELS[user?.role]}</div>
            </div>
            <button onClick={logout} className="text-slate-400 hover:text-white" title="Logout" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-6 gap-4" data-testid="app-header">
          <form onSubmit={handleSearch} className="flex-1 max-w-xl">
            <div className="relative flex items-center gap-1">
              <div className="flex items-center bg-slate-100 rounded-md p-0.5">
                <button type="button" onClick={() => setSearchMode("search")}
                        data-testid="search-mode-search"
                        className={`h-8 px-3 rounded text-xs font-semibold ${searchMode === "search" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Search</button>
                <button type="button" onClick={() => setSearchMode("ai")}
                        data-testid="search-mode-ai"
                        className={`h-8 px-3 rounded text-xs font-semibold flex items-center gap-1 ${searchMode === "ai" ? "bg-white text-[#005289] shadow-sm" : "text-slate-500"}`}>
                  <Sparkles className="w-3 h-3" /> Ask AI
                </button>
              </div>
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="global-search-input"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={searchMode === "ai" ? 'e.g., "contracts over $1M" or "all McKesson contracts"' : "Search contracts, vendors, contract numbers..."}
                  className="w-full h-9 pl-9 pr-3 text-sm bg-slate-50 border border-slate-200 rounded-md focus:bg-white focus:border-[#005289]"
                />
              </div>
            </div>
          </form>
          <AISearchPanel open={aiOpen} initialQuery={aiQuery} onClose={() => setAiOpen(false)} />
          <button
            data-testid="density-toggle"
            onClick={() => setDensity(d => d === "compact" ? "comfortable" : "compact")}
            className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-600"
            title={density === "compact" ? "Switch to comfortable density" : "Switch to compact density"}
            aria-label="Toggle density"
          >
            {density === "compact" ? <Rows4 className="w-4 h-4" /> : <Rows3 className="w-4 h-4" />}
          </button>
          <div className="relative">
            <button data-testid="notifications-btn"
              onClick={() => setNotifOpen(v => !v)}
              className="relative w-9 h-9 flex items-center justify-center rounded-md hover:bg-slate-100"
              aria-label="Notifications"
            >
              <Bell className="w-4 h-4 text-slate-600" />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#EF4444] text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                  {unread}
                </span>
              )}
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-11 w-96 bg-white border border-slate-200 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto custom-scroll">
                <div className="px-4 py-2 border-b border-slate-100 text-xs font-bold uppercase tracking-wider text-slate-500">Notifications</div>
                {notifs.length === 0 && <div className="p-4 text-sm text-slate-500">No notifications</div>}
                {notifs.map(n => (
                  <div key={n.id} className={`px-4 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${!n.read ? "bg-blue-50/30" : ""}`}
                       onClick={() => {
                         if (n.contract_id) {
                           const q = n.note_id ? `?tab=notes&note=${n.note_id}` : "";
                           nav(`/contract/${n.contract_id}${q}`);
                         }
                         setNotifOpen(false);
                       }}>
                    <div className="text-xs font-semibold text-slate-900">{n.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{n.body}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto custom-scroll">
          {children}
        </main>
      </div>
    </div>
    </div>
  );
}
